import { createContext, useContext, type ReactNode } from "react";
import type { UseQueryResult, UseMutationResult } from "@tanstack/react-query";
import type {
  OrgUnitAddressGroup,
  CreateAddressRequest,
  CreateAddressResponse,
  UpdateAddressRequest,
  ValidateAddressResponse,
  AddressFormValues,
  StorefrontAddress,
  CreateAddressGroupParams,
} from "../types";
import type {
  AddressLookupResponse,
  AddressCoordinateLookupResponse,
} from "../../../generated/models";
import type { AddressMapData } from "../../../features/maps-feature/AddressMapProvider";

/**
 * API hooks that the address book feature requires.
 * Each consuming app (CS, CX) provides its own implementation.
 */
export interface AddressBookApi {
  /**
   * Hook to look up address suggestions based on a query string.
   * Used for autocomplete in the street address field.
   */
  useAddressLookup: (query: string) => UseQueryResult<AddressLookupResponse | undefined, Error>;

  /**
   * Hook to get coordinates for an address (geocoding).
   * Used to display the map in the address drawer.
   */
  useAddressCoordinates: (
    input: AddressFormValues | null,
    fetchId?: number
  ) => UseQueryResult<AddressCoordinateLookupResponse | undefined, Error>;

  /**
   * Hook to fetch a server-rendered static map for the supplied coordinates.
   * Each consuming app (CX, CS) wires this to its own maps endpoint so the
   * shared address drawer stays agnostic of the concrete maps backend.
   */
  useAddressMap: (
    latitude?: number | null,
    longitude?: number | null
  ) => UseQueryResult<AddressMapData | undefined, Error>;

  /**
   * Hook to get available address groups for an org unit.
   * Used in the address group multi-select.
   */
  useGetOrgUnitAddressGroups: (
    orgUnitId: string | null,
    includeEmptyGroups?: boolean
  ) => UseQueryResult<OrgUnitAddressGroup[], Error>;

  /**
   * Hook to create a new address group for an org unit.
   * Called when user types a new group name in the multi-select.
   */
  useCreateOrgUnitAddressGroup: () => UseMutationResult<
    OrgUnitAddressGroup,
    Error,
    CreateAddressGroupParams
  >;

  /**
   * Hook to create a new address.
   * Returns mutation with streetAddressId on success.
   */
  useCreateStorefrontAddress: (
    orgUnitId: string | null
  ) => UseMutationResult<CreateAddressResponse, Error, CreateAddressRequest>;

  /**
   * Hook to update an existing address.
   */
  useUpdateStorefrontAddress: (
    orgUnitId: string | null
  ) => UseMutationResult<void, Error, UpdateAddressRequest>;

  /**
   * Hook to validate an address (checks if it can be geocoded).
   * Returns wasExactMatch to determine if confirmation dialog is needed.
   */
  useValidateAddress: () => UseMutationResult<ValidateAddressResponse, Error, AddressFormValues>;

  /**
   * Hook to get all addresses for an org unit.
   * Used by the address grid.
   */
  useGetStorefrontAddresses: (orgUnitId: string | null) => UseQueryResult<StorefrontAddress[], Error>;

  /**
   * Hook to invalidate the storefront addresses query.
   * Called after create/update to refresh the grid.
   */
  useInvalidateStorefrontAddresses: () => () => void;
}

/**
 * Context value that provides both the API hooks and the current org unit ID.
 */
export interface AddressBookContextValue {
  api: AddressBookApi;
  orgUnitId: string | null;
}

const AddressBookContext = createContext<AddressBookContextValue | null>(null);

export interface AddressBookProviderProps {
  children: ReactNode;
  api: AddressBookApi;
  orgUnitId: string | null;
}

/**
 * Provider component that supplies the address book API to child components.
 * Each consuming app wraps its address book pages with this provider,
 * supplying its own API implementation.
 */
export function AddressBookProvider({
  children,
  api,
  orgUnitId,
}: AddressBookProviderProps) {
  return (
    <AddressBookContext.Provider value={{ api, orgUnitId }}>
      {children}
    </AddressBookContext.Provider>
  );
}

/**
 * Hook to access the address book context.
 * Must be used within an AddressBookProvider.
 */
export function useAddressBookContext(): AddressBookContextValue {
  const context = useContext(AddressBookContext);
  if (!context) {
    throw new Error(
      "useAddressBookContext must be used within an AddressBookProvider"
    );
  }
  return context;
}

/**
 * Hook to access just the API from the address book context.
 */
export function useAddressBookApi(): AddressBookApi {
  const { api } = useAddressBookContext();
  return api;
}

/**
 * Hook to access just the org unit ID from the address book context.
 */
export function useAddressBookOrgUnitId(): string | null {
  const { orgUnitId } = useAddressBookContext();
  return orgUnitId;
}
